import {
  Label,
  Language,
  QuestionGroupSortInput,
  RevisionDocumentInput,
  Scalars
} from '@generated/base-types';
import { ManagerError } from '@new/common/utils/error-parser';
import { QuestionGroup } from '@new/pools/lib/types/question-group';
import { PoolIdAndNameFragment } from '../../lib/services/question-groups/duplicate-question-details.generated';

/* Query actions */

export class LoadQuestionGroups {
  public static readonly type = '[QuestionGroups] Load';
}

export class LoadQuestionGroupsSuccess {
  public static readonly type = '[QuestionGroups] Load success';
  constructor(public readonly questionGroups: QuestionGroup[]) {}
}

export class LoadQuestionGroupsFailure {
  public static readonly type = '[QuestionGroups] Load failure';
  constructor(public readonly error: ManagerError) {}
}

export class SetSelectedQuestionGroupIds {
  public static readonly type = '[QuestionGroups] Set selected IDs';
  constructor(public readonly ids: string[]) {}
}

export class ResetSelectedIds {
  public static readonly type = '[QuestionGroups] Reset selected IDs';
}

export class SetSortAttributes {
  public static readonly type = '[QuestionGroups] Set sort attributes';
  constructor(public sortAttributes?: QuestionGroupSortInput[]) {}
}

export class ResetTableState {
  public static readonly type = '[QuestionGroups] Reset table state';
}

export class ResetSortAttributes {
  public static readonly type = '[QuestionGroups] Reset sort attributes';
}

/* Mutation actions */

export class Failure {
  public static readonly type = '[QuestionGroups] Mutation failure';
  constructor(public readonly error: ManagerError) {}
}

export class DeleteQuestionGroups {
  public static readonly type = '[QuestionGroups] Delete QGs';
  constructor(public readonly questionGroupIds: Scalars['ID'][]) {}
}

export class DeleteQuestionGroupsSuccess {
  public static readonly type = '[QuestionGroups] Delete QGs success';
  constructor(public readonly questionGroupIds: Scalars['ID'][]) {}
}

export class SetSupervisor {
  public static readonly type = '[QuestionGroups] Set supervisor';
  constructor(
    public readonly supervisor: string,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetSupervisorSuccess {
  public static readonly type = '[QuestionGroups] Set supervisor success';
  constructor(
    public readonly supervisor: string,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetRevisionYear {
  public static readonly type = '[QuestionGroups] Set revision year';
  constructor(
    public readonly revisionYear: number,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetRevisionYearSuccess {
  public static readonly type = '[QuestionGroups] Set revision year success';
  constructor(
    public readonly revisionYear: number,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetAffiliation {
  public static readonly type = '[QuestionGroups] Set affiliation';
  constructor(
    public readonly affiliationId: Scalars['ID'],
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetAffiliationSuccess {
  public static readonly type = '[QuestionGroups] Set affiliation success';
  constructor(
    public readonly affiliationId: Scalars['ID'],
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetSourceLanguage {
  public static readonly type = '[QuestionGroups] Set source language';
  constructor(
    public readonly language: Language,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetSourceLanguageSuccess {
  public static readonly type = '[QuestionGroups] Set source language success';
  constructor(
    public readonly language: Language,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetBlueprint {
  public static readonly type = '[QuestionGroups] Set blueprint';
  constructor(
    public readonly categories: Scalars['ID'][],
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class SetBlueprintSuccess {
  public static readonly type = '[QuestionGroups] Set blueprint success';
}

export class SetRevisionStatus {
  public static readonly type = '[QuestionGroups] Set revision status';
  constructor(
    public readonly revisionStatusId: Scalars['ID'] | null,
    public readonly questionGroupIds: Scalars['ID'][],
    // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
    public readonly fromFormEditor?: boolean
  ) {}
}

export class SetRevisionStatusSuccess {
  public static readonly type = '[QuestionGroups] Set revision status success';
  constructor(
    public readonly revisionStatusId: Scalars['ID'] | null,
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class CreateRevisionDocument {
  public static readonly type = '[QuestionGroups] Create revision document';
  constructor(public readonly displayOptions: RevisionDocumentInput) {}
}

export class CreateRevisionDocumentSuccess {
  public static readonly type =
    '[QuestionGroups] Create revision document success';
}

export class AssignExam {
  public static readonly type = '[QuestionGroups] Assign exam';
  constructor(
    public readonly questionGroupIds: Scalars['ID'][],
    public readonly examIdsToAdd: Scalars['ID'][],
    public readonly examIdsToRemove: Scalars['ID'][],
    // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
    public readonly fromFormEditor?: boolean
  ) {}
}

export class AssignExamSuccess {
  public static readonly type = '[QuestionGroups] Assign exam success';
  constructor(
    public readonly questionGroupIds: Scalars['ID'][],
    public readonly examIdsToAdd: Scalars['ID'][],
    public readonly examIdsToRemove: Scalars['ID'][]
  ) {}
}

export class AssignQGLabels {
  public static readonly type = '[QuestionGroups] Assign QG labels';
  constructor(
    public readonly labelIdsToAdd: Scalars['ID'][],
    public readonly labelIdsToRemove: Scalars['ID'][],
    public readonly questionGroupIds: Scalars['ID'][],
    // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
    public readonly fromFormEditor?: boolean
  ) {}
}

export class AssignQGLabelsSuccess {
  public static readonly type = '[QuestionGroups] Assign QG labels success';
  constructor(
    public readonly labelIdsToAdd: Scalars['ID'][],
    public readonly labelIdsToRemove: Scalars['ID'][],
    public readonly questionGroupIds: Scalars['ID'][]
  ) {}
}

export class UpdateQGLabel {
  public static readonly type = '[QuestionGroups] Update QG label';
  constructor(public readonly label: Label) {}
}

export class RemoveQGLabel {
  public static readonly type = '[QuestionGroups] Remove QG label';
  constructor(public readonly id: Label['id']) {}
}

export class DuplicateQuestionGroup {
  public static readonly type = '[QuestionGroups] Duplicate question group';
  constructor(
    public readonly questionGroupId: Scalars['ID'],
    public readonly targetPool: PoolIdAndNameFragment,
    public readonly sequentialNumber: number
  ) {}
}

export class DuplicateQuestionGroupSuccess {
  public static readonly type =
    '[QuestionGroups] Duplicate question group success';
  constructor(
    public readonly sequentialNumber: number,
    public readonly targetPoolName: string
  ) {}
}

export class DuplicateQuestionGroupFailure {
  public static readonly type =
    '[QuestionGroups] Duplicate question group failure';
  constructor(
    public readonly error: ManagerError,
    public readonly targetPoolId: Scalars['ID'],
    public readonly questionGroupId: Scalars['ID']
  ) {}
}
